// Copyright 2021 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { Dimension } from "datatypes/SoramaWebApi";
import { ImageExtensions } from "extensions/ImageExtensions";
import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";
import { ArrayPagedRequest } from "libraries/WebRequestHandler/ArrayPagedRequest";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { TransformedPagedRequest } from "libraries/WebRequestHandler/TransformedPagedRequest";
import * as parser from "odata-parser";
import { LocalImageDataRepository } from "repositories/LocalImageDataRepository";
import { LocalImageRepository } from "repositories/LocalImageRepository";
import { AddedImage, IImageService, Image, ImageStorageType, NewImage } from "services/Image/IImageService";

type Transformer<T> = (entities: T[], opts: unknown) => T[];
type OrderByOptions = { [key: string]: "desc" | "asc" }[];

export interface LocalDatabaseImage extends IIndexableObject {
    /** User provided name of the image */
    name: string;

    /** Optional user provided description of the image */
    description?: string;

    /** Dimension of the two dimensional media object in pixels */
    dimension: Dimension;

    /** Time of creation */
    created: Date;
}

@autoinject
export class LocalImageService implements IImageService {
    private static readonly pageSize: number = 10;
    private readonly transformers: { [key: string]: Transformer<LocalDatabaseImage> } = {
        $orderby: (entities: LocalDatabaseImage[], opts: OrderByOptions) => {
            return entities.sort((a, b) => {
                for (let i = 0; i < opts.length; i++) {
                    const opt = opts[i];
                    const propname = Object.getOwnPropertyNames(opt)[0];

                    const aval = a[propname];
                    const bval = b[propname];

                    if (aval !== bval) {
                        const comp = typeof aval === "string" ? aval.localeCompare(bval) : aval < bval ? -1 : 1;
                        return (opt[propname].toLowerCase() === "desc" ? -1 : 1) * comp;
                    }
                }
                return 0;
            });
        },
    };

    constructor(
        private readonly localImageRepository: LocalImageRepository,
        private readonly localImageDataRepository: LocalImageDataRepository
    ) {}

    public getImages(queryFilter?: string): IPagedRequest<Image> {
        const entityPromise = this.localImageRepository.getAll().then((allEntities) => {
            // Parse query filter
            if (queryFilter) {
                let entities = allEntities;

                const queryParams = parser.parse(queryFilter);
                for (const key of Object.getOwnPropertyNames(queryParams)) {
                    const transformer = this.transformers[key];
                    if (!transformer) {
                        console.error("LocalImageService: unsupported odata filter type '" + key + "'.");
                        continue;
                    }

                    entities = transformer(entities, queryParams[key]);
                }

                return entities;
            } else {
                return allEntities;
            }
        });

        const sourceRequest = ArrayPagedRequest.create(entityPromise, LocalImageService.pageSize);
        return TransformedPagedRequest.create(sourceRequest, this.toImage);
    }

    public async getImage(id: string): Promise<Image> {
        const imageEntity = await this.localImageRepository.getById(id);
        return this.toImage(imageEntity);
    }

    public getImageUri(image: Image): Promise<string> {
        return this.localImageDataRepository.get(image.id);
    }

    public async createImage(image: NewImage): Promise<AddedImage> {
        // Get dimensions of the image (this also tests if the image is valid)
        const dimensions = await ImageExtensions.getSize(image.data);

        // Insert image in the database repo
        const localDatabaseImage = await this.localImageRepository.post({
            id: undefined, // Id will be generated on insert
            userId: undefined, // This is not used?
            created: new Date(),
            dimension: dimensions,
            name: image.name,
            description: image.description,
        });

        // We should have an id now
        if (!localDatabaseImage.id) {
            throw new Error("Unable to insert image in local database.");
        }

        // Update image data to the local storage
        try {
            await this.localImageDataRepository.set(localDatabaseImage.id, image.data);
        } catch (e) {
            try {
                // Unable to write image to local storage, delete database entry aswell
                await this.localImageRepository.delete(localDatabaseImage.id);
            } catch (anotherE) {
                // Another error... :/
                console.error("Error deleting local image from database while rolling back insert", anotherE);
            }

            throw e;
        }

        const newImage = (await this.toImage(localDatabaseImage)) as AddedImage;
        newImage.added = true;
        return newImage;
    }

    public deleteImage(id: string): Promise<void> {
        return Promise.all([
            this.localImageRepository
                .delete(id)
                .catch((e) => console.error("Error deleting local image from local database.", e)),
            this.localImageDataRepository
                .delete(id)
                .catch((e) => console.error("Error deleting local image data from local storage.", e)),
        ]).then(() => {});
    }

    // Arrow notation here to capture 'this'
    private toImage = async (image: LocalDatabaseImage): Promise<Image> => {
        // Fetch preview
        let preview: string = undefined;
        try {
            const imageSource = await this.localImageDataRepository.get(image.id);
            preview = await ImageExtensions.generateThumbnail(imageSource, 128);
        } catch (e) {
            console.error("Error loading preview for image", e);
        }

        return {
            id: image.id,
            name: image.name,
            description: image.description,
            dimension: image.dimension,
            type: ImageStorageType.Local,
            created: image.created,
            preview: preview,
        };
    };
}
